import React from 'react';

import moment from 'moment';
import {Options, RRule} from 'rrule';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {gt} from 'app/utils/i18n';

import {CheckboxWithSubFields} from './CheckboxWithSubFields';

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

interface Props {
  options: Options;
  onChange: (options: Partial<Options>) => void;
}

export function MonthSelectFields(
  this: void,
  {options, onChange}: Props
): JSX.Element | null {
  const localizedMonths: Array<{
    val: string;
    key: number;
  }> = React.useMemo(() => {
    return MONTHS.map((key, idx) => ({
      key: key,
      val: moment().month(idx).format('MMM'),
    }));
  }, []);

  const normalizedByMonth = React.useMemo(() => {
    const {bymonth} = options;
    return (
      bymonth == null ? [] : Array.isArray(bymonth) ? [...bymonth] : [bymonth]
    ).sort((a, b) => a - b);
  }, [options.bymonth]);

  const dueDateMonth = React.useMemo(
    () => moment(options.dtstart).month() + 1,
    [options.dtstart]
  );

  React.useEffect(() => {
    if (normalizedByMonth.length === 12) {
      onChange(toggleMonthsSelection(options, true, dueDateMonth));
    }
  }, [normalizedByMonth.length]);

  return (
    <CheckboxWithSubFields
      fieldId="all-months-checkbox"
      label={gt('Repeats every month')}
      checked={normalizedByMonth.length === 0}
      onChange={(checked) =>
        onChange(toggleMonthsSelection(options, checked, dueDateMonth))
      }
      opened={Boolean(options.bymonth)}
    >
      <>
        <label>{gt('Repeats on')}</label>
        <div className="row">
          {localizedMonths.map((month) => (
            <div className="col-2" key={month.key}>
              <LazyTooltip
                key={month.key}
                hide={month.key != dueDateMonth}
                tooltip={gt('This is the due date month')}
              >
                <div className="form-check form-check-inline">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`month-checkbox-${month.key}`}
                    checked={normalizedByMonth.indexOf(month.key) > -1}
                    onChange={(e) =>
                      month.key != dueDateMonth
                        ? onChange(
                            updateMonth(
                              month.key,
                              e.target.checked,
                              normalizedByMonth
                            )
                          )
                        : null
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`month-checkbox-${month.key}`}
                  >
                    {' '}
                    {month.val}
                  </label>
                </div>
              </LazyTooltip>
            </div>
          ))}
        </div>
      </>
    </CheckboxWithSubFields>
  );
}

function toggleMonthsSelection(
  this: void,
  options: Options,
  selectAll: boolean,
  duedateMonth: number
): Partial<Options> {
  let update: Partial<Options> = {};

  if (selectAll && options.freq === RRule.YEARLY) {
    update = {
      freq: RRule.MONTHLY,
      bymonth: undefined,
    };
  } else {
    update = {
      bymonth: selectAll
        ? null // months are zero indexed in moment but not in rrule.js
        : [duedateMonth],
    };
  }
  return update;
}

function updateMonth(
  this: void,
  month: number,
  checked: boolean,
  normalizedByMonth: number[]
): Partial<Options> {
  return {
    bymonth: (checked
      ? normalizedByMonth.concat([month])
      : normalizedByMonth.filter((m) => m !== month)
    ).sort(),
  };
}
